import { useState } from "react";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/oz-move-logo.webp";
import heroTruck from "@/assets/hero-truck.webp";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// Priority order: items hide first as screen shrinks (Reviews → Services → How It Works)
const navLinks = [
  { label: "How It Works", href: "/#how-it-works", showClass: "lg:inline-flex", hideClass: "lg:hidden" },
  { label: "Services", href: "/#services", showClass: "xl:inline-flex", hideClass: "xl:hidden" },
  { label: "Reviews", href: "/#reviews", showClass: "2xl:inline-flex", hideClass: "2xl:hidden" },
  { label: "Get a Quote", href: "/#quote", showClass: "lg:inline-flex", hideClass: "lg:hidden" },
];

const HeroSection = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <section className="bg-[#FFC300] relative overflow-hidden">
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 bg-[#FFC300]">
          <div className="container flex flex-nowrap items-center justify-between gap-4 py-3 whitespace-nowrap">
            {/* LEFT: Hamburger + Logo */}
            <div className="flex flex-nowrap items-center gap-3 min-w-0">
              <button
                className="text-[#1a1a1a] p-1 md:hidden"
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                onClick={() => setMenuOpen(!menuOpen)}
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <a href="/">
                <img src={logo} alt="OZ MOVE CO. Logo" className="h-16 md:h-24 w-auto shrink-0" width="288" height="192" />
              </a>
              {/* Desktop nav links */}
              <nav className="hidden md:flex flex-nowrap items-center gap-6 ml-6">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className={`hidden ${link.showClass} text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap`}
                  >
                    {link.label}
                  </a>
                ))}
                {/* "More" dropdown surfaces nav items hidden at the current breakpoint */}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    aria-label="More navigation"
                    className="hidden md:inline-flex 2xl:hidden items-center gap-1 text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap focus:outline-none"
                  >
                    More <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#FFC300] border-[#1a1a1a]/10">
                    {navLinks
                      .filter((l) => l.label !== "Get a Quote")
                      .map((link) => (
                        <DropdownMenuItem
                          key={link.label}
                          asChild
                          className={`${link.hideClass} text-[#1a1a1a] font-semibold focus:bg-[#1a1a1a]/10 focus:text-[#1a1a1a]`}
                        >
                          <a href={link.href}>{link.label}</a>
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </nav>
            </div>

            {/* RIGHT: Phone → Get Quote → Book Now */}
            <div className="flex flex-nowrap items-center gap-2 md:gap-3 shrink-0">
              <a
                href="tel:1800556654"
                data-call-source="header_desktop"
                aria-label="Call 1800 556 654"
                className="inline-flex items-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-3 md:px-4 py-2 font-bold text-sm rounded-full hover:bg-[#1a1a1a]/5 transition-colors whitespace-nowrap shrink-0"
              >
                <Phone className="w-4 h-4 shrink-0" />
                <span className="hidden lg:inline">1800 556 654</span>
              </a>
              <a
                href="/#quote"
                className="hidden md:inline-flex border-2 border-[#1a1a1a] text-[#1a1a1a] px-4 py-2 font-bold text-sm rounded-full hover:bg-[#1a1a1a]/5 transition-colors whitespace-nowrap shrink-0"
              >
                Get Quote
              </a>
              <a
                href="/#quote"
                className="inline-flex bg-[#1a1a1a] text-[#FFC300] px-4 md:px-5 py-2 font-bold text-sm rounded-full hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
              >
                Book Now
              </a>
            </div>
          </div>

          {/* Mobile nav drawer */}
          {menuOpen && (
            <nav className="md:hidden border-t border-[#1a1a1a]/10 bg-[#FFC300]">
              <div className="container py-4 flex flex-col gap-3">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="text-[#1a1a1a] font-bold text-base py-2 border-b border-[#1a1a1a]/10 last:border-b-0"
                  >
                    {link.label}
                  </a>
                ))}
                <a
                  href="tel:1800556654"
                  data-call-source="header_mobile_menu"
                  className="flex items-center gap-2 text-[#1a1a1a] font-bold text-base py-2"
                >
                  <Phone className="w-5 h-5" />
                  1800 556 654
                </a>
              </div>
            </nav>
          )}
        </header>

        {/* Hero Content */}
        <div className="relative z-10 container">
          <div className="flex flex-col xl:flex-row xl:items-end min-h-[420px] md:min-h-[480px] xl:min-h-[520px]">
            <div className="w-full xl:w-1/2 flex flex-col justify-center items-center xl:items-start text-center xl:text-left py-10 md:py-16 xl:py-20 pr-0 xl:pr-12">
              <h1 className="text-5xl md:text-6xl xl:text-6xl font-black text-[#1a1a1a] leading-[1.08] tracking-tight">
                Move Without the Stress
                <span className="block mt-3 text-xl md:text-2xl xl:text-2xl font-bold text-[#1a1a1a]/80">
                  OZ MOVE CO. — Melbourne Removalists &amp; Moving Company
                </span>
              </h1>
              <p className="mt-8 text-lg md:text-xl xl:text-lg text-[#1a1a1a]/75 max-w-md font-medium leading-relaxed">
                No delays. No hidden costs. Just a reliable team that shows up and gets it done right.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center xl:justify-start w-full sm:w-auto">
                <a
                  href="/#how-it-works"
                  className="border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-md hover:bg-[#1a1a1a]/5 transition-colors text-center"
                >
                  See How It Works
                </a>
                <a
                  href="/#quote"
                  className="bg-[#1a1a1a] text-[#FFC300] px-8 py-4 text-lg font-bold rounded-md hover:opacity-90 transition-opacity text-center"
                >
                  Get a Free Quote
                </a>
              </div>
            </div>
            <div className="w-full xl:w-1/2 relative flex items-end justify-center xl:justify-end mt-4 xl:mt-0">
              <img
                src={heroTruck}
                alt="OZ MOVE CO. moving truck with kangaroo mascot"
                width="1100"
                height="734"
                className="w-[85%] md:w-[70%] xl:w-[120%] max-w-none h-auto object-contain object-bottom xl:object-right-bottom xl:-mr-16 2xl:-mr-24"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Subtle diagonal divider */}
      <div className="relative -mt-1">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block" preserveAspectRatio="none">
          <polygon points="0,0 1440,50 1440,60 0,60" fill="#FFC300" />
        </svg>
      </div>
    </>
  );
};

export default HeroSection;
